import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { useAdminData } from '../hooks/useAdminData';
import Button from '@/common/ui/components/Button';
import Select from '@/common/ui/components/Select';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { columbusPharmacies, type Pharmacy } from '@campusmeds/shared';
import { WorkflowStatus } from '@campusmeds/shared';

interface RefillItem {
  patientName: string;
  medicationName: string;
  pharmacyName: string;
  pharmacyPhone: string;
  daysLeft: number;
  medicationId: string;
  userPhone?: string;
  workflowStatus: WorkflowStatus;
}

type TabType = 'work_queue' | 'awaiting' | 'problems';
type UrgencyFilter = 'urgent' | 'all';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { medications, patients, loading, needsCallCount, awaitingPatientCount, problemCount } = useAdminData();
  const [activeTab, setActiveTab] = useState<TabType>('work_queue');
  const [urgencyFilter, setUrgencyFilter] = useState<UrgencyFilter>('urgent');
  const [selectedMedication, setSelectedMedication] = useState<RefillItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalStep, setModalStep] = useState<'status' | 'pharmacy' | 'timing'>('status');
  const [selectedStatus, setSelectedStatus] = useState<'IN_STOCK' | 'OUT_OF_STOCK' | 'NO_ANSWER' | null>(null);
  const [selectedAlternatePharmacy, setSelectedAlternatePharmacy] = useState<Pharmacy | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickupDate, setPickupDate] = useState(new Date().toISOString().split('T')[0]);

  // Validation utility to check if medication ID is valid
  const isValidMedicationId = (id: string | undefined): boolean => {
    return !!id && typeof id === 'string' && id.trim().length > 0 && !id.includes('undefined') && !id.includes('null');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      // Error will be handled by auth service
    }
  };

  const openModal = (item: RefillItem) => {
    // Validate medication ID before opening modal
    if (!isValidMedicationId(item.medicationId)) {
      alert('Error: This medication record is incomplete (missing ID). Please contact support to fix this record.');
      return;
    }
    setSelectedMedication(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedMedication(null);
    setIsModalOpen(false);
    setIsLoading(false);
    setModalStep('status');
    setSelectedStatus(null);
    setSelectedAlternatePharmacy(null);
    setAdminNote('');
    setShowDatePicker(false);
    setPickupDate(new Date().toISOString().split('T')[0]);
  };

  const updateMedicationStatus = async (status: 'NO_ANSWER') => {
    if (!selectedMedication) return;

    // Validate medication ID before calling Cloud Function
    if (!isValidMedicationId(selectedMedication.medicationId)) {
      alert('Error: This medication record has an invalid ID. Cannot update status.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const updateStatus = httpsCallable(functions, 'updateMedicationStatus');
      await updateStatus({
        medicationId: selectedMedication.medicationId,
        status,
        adminNote: adminNote.trim() || undefined
      });

      closeModal();
    } catch (error) {
      alert(`Failed to update medication status: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const markAsCompleted = async () => {
    if (!selectedMedication || !pickupDate) return;

    // Validate pickup date (must be <= today and >= 90 days ago)
    const selected = new Date(pickupDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    if (selected > today) {
      alert('Pickup date cannot be in the future.');
      return;
    }
    if (selected < ninetyDaysAgo) {
      alert('Pickup date cannot be more than 90 days ago.');
      return;
    }

    if (!isValidMedicationId(selectedMedication.medicationId)) {
      alert('Error: Invalid medication ID.');
      return;
    }

    setIsLoading(true);
    try {
      const markCompleted = httpsCallable(functions, 'markMedicationCompleted');
      await markCompleted({
        medicationId: selectedMedication.medicationId,
        pickupDate: pickupDate,
        adminNote: adminNote.trim() || undefined
      });

      closeModal();
    } catch (error) {
      alert(`Failed to mark as completed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const markAsProblem = async () => {
    if (!selectedMedication) return;

    if (!adminNote.trim()) {
      alert('Please add a note explaining the problem.');
      return;
    }

    if (!isValidMedicationId(selectedMedication.medicationId)) {
      alert('Error: Invalid medication ID.');
      return;
    }

    setIsLoading(true);
    try {
      const markProblem = httpsCallable(functions, 'markMedicationProblem');
      await markProblem({
        medicationId: selectedMedication.medicationId,
        adminNote: adminNote.trim()
      });

      closeModal();
    } catch (error) {
      alert(`Failed to mark as problem: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const moveBackToQueue = async () => {
    if (!selectedMedication) return;

    if (!isValidMedicationId(selectedMedication.medicationId)) {
      alert('Error: Invalid medication ID.');
      return;
    }

    setIsLoading(true);
    try {
      const moveToQueue = httpsCallable(functions, 'moveToWorkQueue');
      await moveToQueue({
        medicationId: selectedMedication.medicationId,
        adminNote: adminNote.trim() || undefined
      });

      closeModal();
    } catch (error) {
      alert(`Failed to move to work queue: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const handleStatusSelect = (status: 'IN_STOCK' | 'OUT_OF_STOCK' | 'NO_ANSWER') => {
    setSelectedStatus(status);
    if (status === 'NO_ANSWER') {
      // Just update database for NO_ANSWER (no SMS sent)
      updateMedicationStatus(status);
    } else if (status === 'OUT_OF_STOCK') {
      // Show pharmacy selection for OUT_OF_STOCK
      setModalStep('pharmacy');
    } else {
      // Show timing options for IN_STOCK
      setModalStep('timing');
    }
  };

  const sendStockNotification = async (status: 'IN_STOCK' | 'OUT_OF_STOCK', timing: 'now' | 'schedule') => {
    if (!selectedMedication) return;

    // Validate medication ID before calling Cloud Function
    if (!isValidMedicationId(selectedMedication.medicationId)) {
      alert('Error: This medication record has an invalid ID. Cannot send notification.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const sendNotification = httpsCallable(functions, 'sendStockNotification');
      await sendNotification({
        medicationId: selectedMedication.medicationId,
        status,
        phone: selectedMedication.userPhone,
        timing,
        // Include medication details for dynamic messages
        medicationName: selectedMedication.medicationName,
        pharmacyName: selectedMedication.pharmacyName,
        pharmacyPhone: selectedMedication.pharmacyPhone,
        // Include alternate pharmacy for OUT_OF_STOCK messages
        alternatePharmacy: selectedAlternatePharmacy ? {
          name: selectedAlternatePharmacy.name,
          address: selectedAlternatePharmacy.address,
          phone: selectedAlternatePharmacy.phone
        } : undefined
      });

      closeModal();
      // Optionally refresh data here
    } catch (error) {
      alert(`Failed to send notification: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  // Calculate medication items based on active tab and filters
  const filteredData = useMemo(() => {
    const now = new Date();
    const items: RefillItem[] = [];

    medications.forEach((medication) => {
      // Skip medications with invalid IDs
      if (!medication.id || typeof medication.id !== 'string' || medication.id.trim() === '') {
        return;
      }

      // Filter by workflow status based on active tab
      if (activeTab === 'work_queue' && medication.workflowStatus !== WorkflowStatus.NEEDS_CALL) return;
      if (activeTab === 'awaiting' && medication.workflowStatus !== WorkflowStatus.AWAITING_PATIENT) return;
      if (activeTab === 'problems' && medication.workflowStatus !== WorkflowStatus.PROBLEM) return;

      const patient = patients.find(p => p.id === medication.userId);
      if (!patient) {
        return;
      }

      // Calculate days left
      let daysLeft = 999; // Default for medications without pickup data
      if (medication.lastPickupDate && medication.daySupply) {
        const pickupDate = new Date(medication.lastPickupDate);
        const runOutDate = new Date(pickupDate);
        runOutDate.setDate(runOutDate.getDate() + medication.daySupply);
        const timeDiff = runOutDate.getTime() - now.getTime();
        daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
      }

      // Apply urgency filter for work queue tab only
      if (activeTab === 'work_queue' && urgencyFilter === 'urgent' && daysLeft > 2) {
        return; // Skip non-urgent items when urgent filter is active
      }

      items.push({
        patientName: patient.email?.split('@')[0] || patient.name || 'Unknown',
        medicationName: `${medication.name} ${medication.dosage || ''}`,
        pharmacyName: medication.pharmacyName || 'Unknown Pharmacy',
        pharmacyPhone: medication.pharmacyPhone || 'N/A',
        daysLeft,
        medicationId: medication.id,
        userPhone: medication.userPhone || patient.phoneNumber || 'N/A',
        workflowStatus: medication.workflowStatus
      });
    });

    // Sort based on tab
    if (activeTab === 'work_queue') {
      items.sort((a, b) => a.daysLeft - b.daysLeft); // Most urgent first
    } else if (activeTab === 'awaiting') {
      items.sort((a, b) => a.daysLeft - b.daysLeft); // Can sort by lastNotification in future
    }

    return items;
  }, [medications, patients, activeTab, urgencyFilter]);

  const getDaysLeftColor = (daysLeft: number) => {
    if (daysLeft < 0) return 'text-red-600 font-bold'; // Overdue
    if (daysLeft <= 1) return 'text-red-500 font-semibold'; // Urgent
    if (daysLeft <= 3) return 'text-orange-500 font-medium'; // Soon
    if (daysLeft <= 7) return 'text-yellow-600'; // Approaching
    return 'text-green-600'; // Good
  };

  const getRefillBadge = (daysLeft: number) => {
    if (daysLeft >= -2 && daysLeft <= 0) {
      return { text: 'Ready to Fill', color: 'bg-green-500 text-white' };
    }
    if (daysLeft < -2) {
      return { text: 'Overdue', color: 'bg-red-500 text-white' };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-title-lg font-inter text-text-primary">Refill Tracking</h1>
              <p className="text-supporting text-text-secondary font-inter">{user?.email}</p>
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => navigate('/admin/knowledge-base')}
                variant="ghost"
                size="md"
              >
                📚 Knowledge Base
              </Button>
              <Button
                onClick={() => navigate('/admin/metrics')}
                variant="ghost"
                size="md"
              >
                View Metrics
              </Button>
              <Button
                onClick={handleSignOut}
                variant="ghost"
                size="md"
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Dashboard */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('work_queue')}
              className={`${
                activeTab === 'work_queue'
                  ? 'border-trust-blue text-trust-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              My Work Queue ({needsCallCount})
            </button>
            <button
              onClick={() => setActiveTab('awaiting')}
              className={`${
                activeTab === 'awaiting'
                  ? 'border-trust-blue text-trust-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Awaiting Patient Response ({awaitingPatientCount})
            </button>
            <button
              onClick={() => setActiveTab('problems')}
              className={`${
                activeTab === 'problems'
                  ? 'border-trust-blue text-trust-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Problem Cases ({problemCount})
            </button>
          </nav>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium text-gray-900">
                {activeTab === 'work_queue' && 'My Work Queue'}
                {activeTab === 'awaiting' && 'Awaiting Patient Response'}
                {activeTab === 'problems' && 'Problem Cases'}
                {' '}({filteredData.length} medications)
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {activeTab === 'work_queue' && 'Medications that need pharmacy calls'}
                {activeTab === 'awaiting' && 'Waiting for patient pickup confirmation'}
                {activeTab === 'problems' && 'Medications requiring manual intervention'}
              </p>
            </div>

            {/* Urgency Filter (Work Queue only) */}
            {activeTab === 'work_queue' && (
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Filter:</label>
                <Select
                  value={urgencyFilter}
                  onChange={(e) => setUrgencyFilter(e.target.value as UrgencyFilter)}
                  options={[
                    { value: 'urgent', label: 'Urgent Only (≤2 days)' },
                    { value: 'all', label: 'Show All' }
                  ]}
                  className="w-48"
                />
              </div>
            )}
          </div>

          {filteredData.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              {activeTab === 'work_queue' && 'No medications need calls right now.'}
              {activeTab === 'awaiting' && 'No patients awaiting pickup confirmation.'}
              {activeTab === 'problems' && 'No problem cases at the moment.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Medication
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pharmacy
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days Left
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.patientName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.medicationName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.pharmacyName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <a href={`tel:${item.userPhone}`} className="text-trust-blue hover:text-trust-blue/80 transition-colors duration-200">
                          {item.userPhone || 'N/A'}
                        </a>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${item.daysLeft === 999 ? 'text-gray-400' : getDaysLeftColor(item.daysLeft)}`}>
                        <div className="flex items-center gap-2">
                          <span>
                            {item.daysLeft === 999
                              ? 'N/A'
                              : item.daysLeft < 0
                                ? `${Math.abs(item.daysLeft)} days overdue`
                                : item.daysLeft === 0
                                  ? 'Today'
                                  : `${item.daysLeft} days`}
                          </span>
                          {item.daysLeft === 999 ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-700">
                              Setup Needed
                            </span>
                          ) : getRefillBadge(item.daysLeft) ? (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRefillBadge(item.daysLeft)!.color}`}>
                              {getRefillBadge(item.daysLeft)!.text}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => openModal(item)}
                          className="bg-trust-blue hover:bg-trust-blue/80 text-white px-3 py-1 rounded text-xs font-medium transition-colors duration-200"
                        >
                          Check Status
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* Stock Status Modal */}
      {isModalOpen && selectedMedication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-medium text-gray-900">Check Stock Status</h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Patient:</strong> {selectedMedication.patientName}
              </p>
              {selectedMedication.userPhone && (
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Phone:</strong> <a href={`tel:${selectedMedication.userPhone}`} className="text-trust-blue hover:text-trust-blue/80">{selectedMedication.userPhone}</a>
                </p>
              )}
              <p className="text-sm text-gray-600 mb-2">
                <strong>Medication:</strong> {selectedMedication.medicationName}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Pharmacy:</strong> {selectedMedication.pharmacyName}
              </p>
            </div>

{modalStep === 'status' ? (
              <div className="flex flex-col gap-3">
                {/* Admin Notes Field */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes about this call (optional):
                  </label>
                  <textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Add any relevant notes..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-trust-blue focus:border-trust-blue text-sm"
                  />
                </div>

                {/* Primary Actions */}
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs text-gray-500 mb-2 font-medium">Call Pharmacy Result:</p>
                  <button
                    onClick={() => handleStatusSelect('IN_STOCK')}
                    disabled={isLoading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                  >
                    In Stock - Ready for Pickup
                  </button>

                  <button
                    onClick={() => handleStatusSelect('OUT_OF_STOCK')}
                    disabled={isLoading}
                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                  >
                    Out of Stock - Will Notify
                  </button>

                  <button
                    onClick={() => handleStatusSelect('NO_ANSWER')}
                    disabled={isLoading}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Updating...' : 'Did not Answer/Left VM'}
                  </button>
                </div>

                {/* Manual Overrides */}
                <div className="border-t border-gray-200 pt-3 mt-2">
                  <p className="text-xs text-gray-500 mb-2 font-medium">Manual Actions:</p>

                  {/* Mark as Completed */}
                  {!showDatePicker ? (
                    <button
                      onClick={() => setShowDatePicker(true)}
                      disabled={isLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-2 text-sm"
                    >
                      Mark as Completed
                    </button>
                  ) : (
                    <div className="mb-2 p-3 bg-blue-50 rounded">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Date:
                      </label>
                      <input
                        type="date"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-trust-blue focus:border-trust-blue text-sm mb-2"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={markAsCompleted}
                          disabled={isLoading}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          {isLoading ? 'Saving...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setShowDatePicker(false)}
                          className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-1 rounded text-sm font-medium transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Mark as Problem */}
                  <button
                    onClick={markAsProblem}
                    disabled={isLoading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-2 text-sm"
                  >
                    Mark as Problem
                  </button>

                  {/* Back to Queue (only show in Problem Cases tab) */}
                  {selectedMedication.workflowStatus === WorkflowStatus.PROBLEM && (
                    <button
                      onClick={moveBackToQueue}
                      disabled={isLoading}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Resolved - Back to Queue
                    </button>
                  )}
                </div>
              </div>
            ) : modalStep === 'pharmacy' ? (
              <div className="flex flex-col gap-3">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Status: <strong className="text-gray-900">Out of Stock - Will Notify</strong>
                  </p>
                  <Select
                    label="Select an alternative pharmacy to recommend:"
                    value={selectedAlternatePharmacy?.id || ''}
                    onChange={(e) => {
                      const pharmacy = columbusPharmacies.find(p => p.id === e.target.value);
                      setSelectedAlternatePharmacy(pharmacy || null);
                    }}
                    options={[
                      { value: '', label: 'Select alternate pharmacy...' },
                      ...columbusPharmacies.map((pharmacy) => ({
                        value: pharmacy.id,
                        label: `${pharmacy.name} - ${pharmacy.address}`
                      }))
                    ]}
                  />
                </div>

                <button
                  onClick={() => setModalStep('timing')}
                  disabled={!selectedAlternatePharmacy}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>

                <button
                  onClick={() => setModalStep('status')}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded font-medium transition-colors duration-200"
                >
                  Back
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Status: <strong className="text-gray-900">{selectedStatus === 'IN_STOCK' ? 'In Stock - Ready for Pickup' : 'Out of Stock - Will Notify'}</strong>
                  </p>
                  {selectedAlternatePharmacy && (
                    <p className="text-sm text-gray-600 mt-2">
                      Alternate Pharmacy: <strong className="text-gray-900">{selectedAlternatePharmacy.name}</strong>
                    </p>
                  )}
                </div>

                <button
                  onClick={() => selectedStatus !== 'NO_ANSWER' && sendStockNotification(selectedStatus!, 'now')}
                  disabled={isLoading || selectedStatus === 'NO_ANSWER'}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending...' : 'Send'}
                </button>

                <button
                  onClick={() => selectedStatus !== 'NO_ANSWER' && sendStockNotification(selectedStatus!, 'schedule')}
                  disabled={isLoading || selectedStatus === 'NO_ANSWER'}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Scheduling...' : 'Schedule Send'}
                </button>

                <button
                  onClick={() => setModalStep(selectedStatus === 'OUT_OF_STOCK' ? 'pharmacy' : 'status')}
                  disabled={isLoading}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded font-medium transition-colors duration-200"
                >
                  Back
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}